import type { Dayjs, OpUnitType } from 'dayjs';

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import 'dayjs/locale/pt-br';

// ----------------------------------------------------------------------

/**
 * @Docs
 * https://day.js.org/docs/en/display/format
 */

/** Default timezones https://day.js.org/docs/en/timezone/set-default-timezone#docsNav */

/**
 * UTC https://day.js.org/docs/en/plugin/utc
 *
 * @install
 * import utc from 'dayjs/plugin/utc';
 * dayjs.extend(utc);
 * @usage
 * dayjs().utc().format()
 */

dayjs.extend(customParseFormat);
dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('pt-br');

// ----------------------------------------------------------------------

export type DatePickerFormat = Dayjs | Date | string | number | null | undefined;

export const formatPatterns = {
  dateTime: 'DD MMM YYYY HH:mm', // 17 Abr 2022 21:45
  date: 'DD MMM YYYY', // 17 Abr 2022
  time: 'HH:mm', // 21:45,
  isoString: 'YYYY-MM-DDTHH:mm:ss.SSSZ', // 2022-04-17T14:45:31-03:00
  split: {
    dateTime: 'DD/MM/YYYY HH:mm', // 17/04/2022 21:45
    date: 'DD/MM/YYYY', // 17/04/2022
    time: 'HH:mm', // 21:45,
    fulltime: 'HH:mm:ss' // 21:45:31
  },
  paramCase: {
    dateTime: 'DD-MM-YYYY HH:mm', // 17-04-2022 21:45
    date: 'DD-MM-YYYY' // 17-04-2022
  }
};

const isValidDate = (date: DatePickerFormat) => date !== null && date !== undefined && dayjs(date).isValid();

const DEFAULT_TIMEZONE = 'America/Sao_Paulo';

// ----------------------------------------------------------------------

/** @output 2022-04-17T14:45:31-03:00 */
export function fNow(tz: string = 'America/Sao_Paulo'): string {
  return dayjs().tz(tz).format(formatPatterns.isoString);
}

export function fToDate(date: string): Date {
  const utcDate = new Date(date);

  const year = utcDate.getUTCFullYear();
  const month = utcDate.getUTCMonth();
  const day = utcDate.getUTCDate();
  const hours = utcDate.getUTCHours();
  const minutes = utcDate.getUTCMinutes();
  const seconds = utcDate.getUTCSeconds();
  const ms = utcDate.getUTCMilliseconds();

  return new Date(year, month, day, hours, minutes, seconds, ms);
}

// ----------------------------------------------------------------------

export function today(template?: string): string {
  return dayjs(new Date()).tz(DEFAULT_TIMEZONE).startOf('day').format(template);
}

// ----------------------------------------------------------------------

/** @output 17 Apr 2022 12:00 am */
export function fDateTime(date: DatePickerFormat, template?: string): string {
  if (!isValidDate(date)) {
    return 'Invalid date';
  }

  // Parse the date and ensure it's in the local timezone
  return dayjs(date)
    .tz()
    .format(template ?? formatPatterns.date);
}

// ----------------------------------------------------------------------

/**
 * Data/hora no fuso da fazenda. App Electron roda com TZ=UTC, então `.tz()` sem argumento sai em UTC.
 *
 * @output 17/04/2022 21:45
 */
export function fDateTimeTz(date: DatePickerFormat, template?: string, tz: string = DEFAULT_TIMEZONE): string {
  if (!isValidDate(date)) {
    return 'Invalid date';
  }

  return dayjs(date)
    .tz(tz)
    .format(template ?? formatPatterns.split.dateTime);
}

// ----------------------------------------------------------------------

/** @output 17 Apr 2022 */
export function fDate(date: DatePickerFormat, template?: string): string {
  if (!isValidDate(date)) {
    return 'Invalid date';
  }

  return dayjs(date)
    .tz(DEFAULT_TIMEZONE)
    .format(template ?? formatPatterns.date);
}

export function fDateUTC(date: DatePickerFormat, template?: string): string {
  if (!isValidDate(date)) {
    return 'Invalid date';
  }

  return dayjs(date)
    .utc()
    .format(template ?? formatPatterns.date);
}

// ----------------------------------------------------------------------

/** @output 12:00 am */
export function fTime(date: DatePickerFormat, template?: string): string {
  if (!isValidDate(date)) {
    return 'Invalid date';
  }

  return dayjs(date).format(template ?? formatPatterns.time);
}

// ----------------------------------------------------------------------

/**
 * Extrai HH:mm do campo `hour` do trato, que vem como ISO UTC (ex: 1970-01-01T06:00:00.000Z). Lê o componente UTC
 * diretamente para não sofrer conversão de fuso. @output 06:00
 */
export function fTreatHour(date: DatePickerFormat): string {
  if (!isValidDate(date)) {
    return '--:--';
  }

  return dayjs.utc(date).format(formatPatterns.time);
}

// ----------------------------------------------------------------------

/** @output 1713250100 */
export function fTimestamp(date: DatePickerFormat): number | 'Invalid date' {
  if (!isValidDate(date)) {
    return 'Invalid date';
  }

  return dayjs(date).valueOf();
}

// ----------------------------------------------------------------------

/** @output a few seconds, 2 years */
export function fToNow(date: DatePickerFormat, tz: string = 'America/Sao_Paulo'): string {
  if (!isValidDate(date)) {
    return 'Invalid date';
  }

  return dayjs(date).tz(tz).toNow(true);
}

// ----------------------------------------------------------------------

/** @output boolean */
export function fIsBetween(
  inputDate: DatePickerFormat,
  startDate: DatePickerFormat,
  endDate: DatePickerFormat
): boolean {
  if (!isValidDate(inputDate) || !isValidDate(startDate) || !isValidDate(endDate)) {
    return false;
  }

  const formattedInputDate = fTimestamp(inputDate);
  const formattedStartDate = fTimestamp(startDate);
  const formattedEndDate = fTimestamp(endDate);

  if (
    formattedInputDate === 'Invalid date' ||
    formattedStartDate === 'Invalid date' ||
    formattedEndDate === 'Invalid date'
  ) {
    return false;
  }

  return formattedInputDate >= formattedStartDate && formattedInputDate <= formattedEndDate;
}

// ----------------------------------------------------------------------

/** @output boolean */
export function fIsAfter(startDate: DatePickerFormat, endDate: DatePickerFormat): boolean {
  if (!isValidDate(startDate) || !isValidDate(endDate)) {
    return false;
  }

  return dayjs(startDate).isAfter(endDate);
}

// ----------------------------------------------------------------------

/** @output boolean */
export function fIsSame(startDate: DatePickerFormat, endDate: DatePickerFormat, unitToCompare?: OpUnitType): boolean {
  if (!isValidDate(startDate) || !isValidDate(endDate)) {
    return false;
  }

  return dayjs(startDate).isSame(endDate, unitToCompare ?? 'year');
}

/**
 * @output
 * Same day: 26 Apr 2024
 * Same month: 25 - 26 Apr 2024
 * Same month: 25 - 26 Apr 2024
 * Same year: 25 Apr - 26 May 2024
 */
export function fDateRangeShortLabel(
  startDate: DatePickerFormat,
  endDate: DatePickerFormat,
  initial?: boolean
): string {
  if (!isValidDate(startDate) || !isValidDate(endDate) || fIsAfter(startDate, endDate)) {
    return 'Invalid date';
  }

  let label = `${fDate(startDate)} - ${fDate(endDate)}`;

  if (initial) {
    return label;
  }

  const isSameYear = fIsSame(startDate, endDate, 'year');
  const isSameMonth = fIsSame(startDate, endDate, 'month');
  const isSameDay = fIsSame(startDate, endDate, 'day');

  if (isSameYear && !isSameMonth) {
    label = `${fDate(startDate, 'DD MMM')} - ${fDate(endDate)}`;
  } else if (isSameYear && isSameMonth && !isSameDay) {
    label = `${fDate(startDate, 'DD')} - ${fDate(endDate)}`;
  } else if (isSameYear && isSameMonth && isSameDay) {
    label = `${fDate(endDate)}`;
  }

  return label;
}

// ----------------------------------------------------------------------

/** @output 2024-05-28T05:55:31+00:00 */
export type DurationProps = {
  years?: number;
  months?: number;
  days?: number;
  hours?: number;
  minutes?: number;
  seconds?: number;
  milliseconds?: number;
};

export function fAdd({
  years = 0,
  months = 0,
  days = 0,
  hours = 0,
  minutes = 0,
  seconds = 0,
  milliseconds = 0
}: DurationProps) {
  const result = dayjs()
    .add(
      dayjs.duration({
        years,
        months,
        days,
        hours,
        minutes,
        seconds,
        milliseconds
      })
    )
    .format();

  return result;
}

/** @output 2024-05-28T05:55:31+00:00 */
export function fSub({
  years = 0,
  months = 0,
  days = 0,
  hours = 0,
  minutes = 0,
  seconds = 0,
  milliseconds = 0
}: DurationProps) {
  const result = dayjs()
    .subtract(
      dayjs.duration({
        years,
        months,
        days,
        hours,
        minutes,
        seconds,
        milliseconds
      })
    )
    .format();

  return result;
}

/**
 * Subtrai uma quantidade de dias de uma data UTC
 *
 * @example FSubDaysUtc('2024-05-28T05:55:31+00:00', 1) -> '2024-05-27T05:55:31+00:00'
 */
export function fSubDays(dateUtc: string, days: number): string {
  if (!dateUtc || !dayjs(dateUtc).isValid()) {
    return dateUtc;
  }

  const result = dayjs.utc(dateUtc).subtract(days, 'day');

  return result.toISOString();
}

// ----------------------------------------------------------------------

/**
 * Calcula a diferença entre duas datas
 *
 * @example FDateBetween("2023-01-15T00:00:00.000Z", "2023-03-10T00:00:00.000Z", "month")
 *
 * @output 1
 */
export function fDateBetween(startDateISO: string, endDateISO: string, unit: dayjs.QUnitType): number {
  if (!isValidDate(startDateISO) || !isValidDate(endDateISO)) {
    return 0;
  }

  const startDate = dayjs(startDateISO);
  const endDate = dayjs(endDateISO);

  return endDate.diff(startDate, unit);
}

/**
 * Converte a data (tipicamente "DD/MM/AAAA" do Excel/local) no calendário da fazenda (America/Sao_Paulo) para instante
 * ISO no início desse dia local, alinhado a {@link fDate}. Requer o plugin `customParseFormat` do dayjs carregado antes
 * do de timezone.
 */
export function fToUtcStartOfDay(dateStr: string): string | 'Invalid date' {
  const s = String(dateStr).trim();
  if (!s || s === 'undefined' || s === 'null' || s === 'Invalid date') {
    return 'Invalid date';
  }

  if (/^\d{4}-\d{2}-\d{2}/.test(s) || s.includes('T')) {
    const d = dayjs(s).tz(DEFAULT_TIMEZONE);
    if (!d.isValid()) return 'Invalid date';
    return d.startOf('day').toISOString();
  }

  const d = dayjs.tz(s, 'DD/MM/YYYY', DEFAULT_TIMEZONE);
  if (!d.isValid()) {
    const alt = dayjs.tz(s, 'D/M/YYYY', DEFAULT_TIMEZONE);
    if (!alt.isValid()) return 'Invalid date';
    return alt.startOf('day').toISOString();
  }
  return d.startOf('day').toISOString();
}

/** Início do dia no calendário da fazenda (America/Sao_Paulo) como ISO UTC. */
export function fBrazilStartOfDayISO(date: DatePickerFormat): string | 'Invalid date' {
  if (!isValidDate(date)) return 'Invalid date';
  return dayjs(date).tz(DEFAULT_TIMEZONE).startOf('day').toISOString();
}

/** Fim do dia no calendário da fazenda (America/Sao_Paulo) como ISO UTC. */
export function fBrazilEndOfDayISO(date: DatePickerFormat): string | 'Invalid date' {
  if (!isValidDate(date)) return 'Invalid date';
  return dayjs(date).tz(DEFAULT_TIMEZONE).endOf('day').toISOString();
}

/**
 * Retorna uma data subtraindo um número de dias, com base em um parâmetro. Útil para filtros "desde X dias atrás".
 *
 * @param sinceParam - O número de dias a subtrair (ex: '7', 7).
 * @param isoDay - A data de referência em formato ISO.
 * @returns A nova data em formato ISO string (UTC), a data original, ou undefined se o parâmetro `since` for inválido.
 */
export function fGetSince(sinceParam: string | number | null, isoDay: string): string | undefined {
  if (!sinceParam || sinceParam === 'undefined') {
    return undefined;
  }

  const since = Number(sinceParam);

  if (Number.isNaN(since) || since <= 0) {
    return isoDay;
  }

  if (!isValidDate(isoDay)) {
    return isoDay;
  }

  return dayjs.utc(isoDay).subtract(since, 'day').toISOString();
}
